import type { BrandTwoLetterCode, RegionCode } from "brandRegionConfig";
import type { ThemeOptions } from "@mui/material";
import Authorize from "core/components/Authorize";
import { GlobalLoader } from "core/components/GlobalLoader";
import { ContextVerifier } from "core/components/GlobalLoader/ContextVerifier";
import { Layout } from "core/components/Layout";
import { LayoutBypass } from "core/components/LayoutBypass";
import { PageErrorBoundary } from "core/components/PageErrorBoundary";
import { RouterLocale } from "core/components/RouterLocale";
import { SharedAppWrapper } from "core/components/SharedAppWrapper";
import { PitstopConfigurationProvider } from "core/context/AppConfigurationContext";
import { KeyContext } from "core/context/KeyContext";
import { RegionContextProvider } from "core/context/RegionContextProvider";
import { SWRConfigContextProvider } from "core/context/SWRConfigContextProvider";
import { useInitializeGlobals } from "core/hooks/useInitializeGlobals";
import { CustomThemePicker } from "core/pages/LandingPage/CustomThemePicker";
import type { FullClientRuntimeConfig } from "core/utils/getClientRuntimeConfig";
import type { NextComponentType, NextPageContext } from "next";
import { useRouter } from "next/router";
import { useState } from "react";
import { PagePermissionChecker } from "./PagePermissionChecker";

export type PitstopApplicationContext = {
  applicationId: "pitstop";
  brandTwoLetterCode: BrandTwoLetterCode;
  availableRegions: RegionCode[];
  environmentVariables: FullClientRuntimeConfig;
  requestInfo: {
    origin: string;
    host: string;
  };
};

/**
 * PitstopApp is an internal Hub application component that serves as the main
 * entry point for the Pitstop application. It handles the overall structure,
 * authentication, and routing for the application.
 */
export function PitstopApp({
  Component,
  pageProps,
  applicationContext,
}: {
  Component: NextComponentType<NextPageContext, any, any>;
  pageProps: any;
  applicationContext: PitstopApplicationContext;
}) {
  const forceRemountKeyState = useState(0);
  const [forceRemountKey] = forceRemountKeyState;
  const {
    query: { customizeTheme },
  } = useRouter();
  const [customTheme, setCustomTheme] = useState<ThemeOptions | null>(null);

  useInitializeGlobals({
    centrixApiUrl: applicationContext.environmentVariables.API_URL!,
    appInsightsConnectionString:
      applicationContext.environmentVariables
        .APPLICATIONINSIGHTS_CONNECTION_STRING!,
  });

  return (
    <KeyContext.Provider value={forceRemountKeyState}>
      <PitstopConfigurationProvider
        config={applicationContext.environmentVariables}
      >
        <SWRConfigContextProvider key={forceRemountKey}>
          <SharedAppWrapper
            brandTwoLetterCode={applicationContext.brandTwoLetterCode}
            customTheme={customTheme}
            availableRegions={applicationContext.availableRegions}
          >
            {customizeTheme === "true" && (
              <CustomThemePicker setCustomTheme={setCustomTheme} />
            )}
            <RegionContextProvider>
              <Authorize origin={applicationContext.requestInfo.origin}>
                <RouterLocale />
                <LayoutBypass Component={Component} pageProps={pageProps}>
                  <ContextVerifier>
                    <GlobalLoader>
                      <Layout>
                        <PagePermissionChecker>
                          <PageErrorBoundary>
                            <Component {...pageProps} />
                          </PageErrorBoundary>
                        </PagePermissionChecker>
                      </Layout>
                    </GlobalLoader>
                  </ContextVerifier>
                </LayoutBypass>
              </Authorize>
            </RegionContextProvider>
          </SharedAppWrapper>
        </SWRConfigContextProvider>
      </PitstopConfigurationProvider>
    </KeyContext.Provider>
  );
}
